/**
 * /moniteur/dashboard — Vue d'ensemble moniteur.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, ClipboardCheck, TrendingUp, ArrowRight, Clock, FileText, Printer, QrCode, ExternalLink, X, Copy, Check, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useApp } from '../../contexts/AppContext';
import api from '../../api';

export default function MoniteurDashboardPage() {
  const { user } = useApp() || {};
  const [lessonsToday, setLessonsToday] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareFiche, setShareFiche] = useState(null); // { title, eleveUrl }

  useEffect(() => {
    document.title = 'Tableau de bord · Espace enseignant';
    (async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const r = await api.boListLessons({ dateFrom: today, dateTo: today });
        setLessonsToday(r?.items || []);
      } catch (_) { /* silent */ }
      setLoading(false);
    })();
  }, []);

  const firstName = (user?.name || 'moniteur').split(' ')[0];
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://code2roues.fr';

  const FICHES = [
    {
      key: 'livret-plateau',
      title: 'Livret plateau A2 — LA référence',
      desc: 'Guide 2-roues complet + 19 compétences à cocher · Version imprimable A4 + interactive mobile.',
      badge: 'LIVRET',
      badgeBg: 'bg-violet-600',
      gradient: 'from-violet-600/25 via-violet-600/10 to-transparent',
      accent: 'text-violet-400',
      hover: 'hover:border-violet-500/40',
      ficheUrl: '/fiches/moto/livret-progression-plateau-a2',
      eleveUrl: `${origin}/fiches/eleve/livret-plateau-a2`,
    },
    {
      key: 'circulation',
      title: '1ʳᵉ leçon moto — Circulation A2',
      desc: 'Fiche route A4 : passage plateau → route, 5 étapes + 6 clés (regard, trajectoire…).',
      badge: 'CIRCULATION',
      badgeBg: 'bg-sky-600',
      gradient: 'from-sky-600/25 via-sky-600/10 to-transparent',
      accent: 'text-sky-400',
      hover: 'hover:border-sky-500/40',
      ficheUrl: '/fiches/moto/premiere-circulation-a2',
      eleveUrl: `${origin}/fiches/eleve/premiere-circulation-a2`,
    },
    {
      key: 'plateau-interactif',
      title: 'Plateau A2 — Parcours interactifs',
      desc: 'Version numérique : les 2 parcours (lent / rapide), temps, barèmes.',
      badge: 'INTERACTIF',
      badgeBg: 'bg-emerald-600',
      gradient: 'from-emerald-500/20 via-emerald-500/10 to-transparent',
      accent: 'text-emerald-400',
      hover: 'hover:border-emerald-500/40',
      ficheUrl: '/fiches/moto/plateau-parcours-a2',
      eleveUrl: `${origin}/fiches/moto/plateau-parcours-a2`,
    },
  ];

  return (
    <div className="max-w-5xl" data-testid="moniteur-dashboard">
      <div className="mb-6">
        <div className="text-[10.5px] font-black uppercase tracking-[0.22em] text-rose-500 mb-2">
          Bonjour {firstName}
        </div>
        <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Tableau de bord
        </h1>
        <p className="text-sm text-white/60 mt-2">Ta synthèse du jour : élèves, leçons à venir, bilans à rédiger.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="mo-kpi">
        <KpiCard icon={Calendar} label="Leçons aujourd’hui" value={loading ? '…' : lessonsToday.length} color="#f43f5e" to="/moniteur/planning" />
        <KpiCard icon={Users} label="Mes élèves" value={'—'} color="#0ea5e9" to="/moniteur/eleves" />
        <KpiCard icon={ClipboardCheck} label="Bilans à faire" value={'—'} color="#f59e0b" to="/moniteur/bilans" />
        <KpiCard icon={TrendingUp} label="Progression" value={'—'} color="#10b981" to="/moniteur/progression" />
      </div>

      <h2 className="text-lg sm:text-xl font-black text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Accès rapides</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <QuickAction to="/moniteur/planning" icon={Calendar} label="Voir mon planning" />
        <QuickAction to="/enseignant/salle" icon={Users} label="Cours en salle" />
        <QuickAction to="/enseignant/terrain" icon={Users} label="Fiches plateau" />
        <QuickAction to="/enseignant/seance-circulation" icon={Users} label="Séances circulation" />
        <QuickAction to="/enseignant/seances-live" icon={Users} label="Séances live récentes" />
        <QuickAction to="/moniteur/bilans" icon={ClipboardCheck} label="Rédiger un bilan" />
      </div>

      {/* ═══════ Fiches pédagogiques imprimables ═══════ */}
      <div className="mt-8">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-lg sm:text-xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Fiches pédagogiques à partager
          </h2>
          <span className="text-[10.5px] text-white/45 uppercase tracking-widest font-bold">1 clic · Partage QR élève</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="mo-fiches">
          {FICHES.map((f) => (
            <FicheCard
              key={f.key}
              fiche={f}
              onShare={() => setShareFiche(f)}
            />
          ))}
        </div>
      </div>

      {/* Modale de partage QR (rendue depuis le dashboard) */}
      {shareFiche && (
        <ShareFicheQRModal fiche={shareFiche} onClose={() => setShareFiche(null)} />
      )}
    </div>
  );
}

function FicheCard({ fiche, onShare }) {
  const { title, desc, badge, badgeBg, gradient, accent, hover, ficheUrl } = fiche;
  return (
    <div
      className={`group relative rounded-2xl border border-white/10 ${hover} bg-gradient-to-br ${gradient} p-5 transition overflow-hidden flex flex-col`}
      data-testid="mo-fiche-card"
    >
      <div className="absolute top-3 right-3">
        <span className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest ${badgeBg} text-white px-2 py-1 rounded-full`}>
          {badge}
        </span>
      </div>
      <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center mb-3">
        <FileText size={18} className={accent} />
      </div>
      <div className="text-white text-[16px] font-black leading-tight mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        {title}
      </div>
      <div className="text-white/60 text-[12.5px] leading-snug mb-4 flex-1">{desc}</div>

      {/* 2 boutons d'action : Ouvrir + Partager QR */}
      <div className="grid grid-cols-2 gap-2 mt-auto">
        <a
          href={ficheUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 text-white text-[12px] font-bold transition"
          data-testid="btn-fiche-open"
        >
          <ExternalLink size={13} /> Ouvrir
        </a>
        <button
          onClick={onShare}
          className={`inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg ${badgeBg} hover:opacity-90 text-white text-[12px] font-bold shadow-lg transition`}
          data-testid="btn-fiche-share-qr"
        >
          <QrCode size={13} /> Partager (QR)
        </button>
      </div>
    </div>
  );
}

function ShareFicheQRModal({ fiche, onClose }) {
  const [copied, setCopied] = useState(false);
  const url = fiche.eleveUrl;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* silent */ }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: fiche.title, text: 'Ta fiche code2roues', url }); }
      catch (_) { /* cancelled */ }
    } else {
      copyUrl();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="modal-share-fiche"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`${fiche.badgeBg} text-white px-5 py-3 flex items-center justify-between`}>
          <div>
            <div className="text-[9px] font-black uppercase tracking-[0.22em] text-white/70">Partage rapide élève</div>
            <div className="text-[14px] font-bold">{fiche.title.replace('1ʳᵉ leçon moto — ', '')}</div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-1"
            data-testid="btn-close-share"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 flex flex-col items-center">
          <div className="bg-white p-3 rounded-lg border-2 border-neutral-200">
            <QRCodeSVG value={url} size={220} level="H" includeMargin={false} bgColor="#ffffff" fgColor="#0a0a0b" />
          </div>
          <div className="mt-4 text-center">
            <div className="text-[11px] text-neutral-500 uppercase tracking-widest font-bold mb-1">Scan élève</div>
            <div className="text-[13px] text-neutral-800">Ton élève accède à la fiche en 1 clic (consulter · sauvegarder · imprimer)</div>
          </div>
          <div className="mt-4 w-full flex items-center gap-2 border border-neutral-300 rounded-lg px-3 py-2 bg-neutral-50">
            <div className="flex-1 text-[11px] text-neutral-700 truncate font-mono" data-testid="share-url">{url}</div>
            <button
              onClick={copyUrl}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold transition ${
                copied ? 'bg-emerald-600 text-white' : 'bg-neutral-900 text-white hover:bg-neutral-700'
              }`}
              data-testid="btn-copy-share-url"
            >
              {copied ? <><Check size={11} /> Copié</> : <><Copy size={11} /> Copier</>}
            </button>
          </div>
          <button
            onClick={nativeShare}
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white text-[12px] font-bold transition"
            data-testid="btn-native-share"
          >
            <Share2 size={13} /> Partager par SMS · WhatsApp · Email
          </button>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color, to }) {
  return (
    <Link to={to} className="rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20 p-4 transition" data-testid="mo-kpi-card">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2" style={{ background: `${color}22`, color }}><Icon size={15} /></div>
      <div className="text-2xl font-black text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold mt-1">{label}</div>
    </Link>
  );
}

function QuickAction({ to, icon: Icon, label }) {
  return (
    <Link to={to} className="rounded-xl border border-white/10 bg-white/[0.02] hover:bg-rose-500/10 hover:border-rose-500/30 px-4 py-3.5 transition group flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-rose-500/15 text-rose-400 flex items-center justify-center"><Icon size={14} /></div>
      <div className="text-sm text-white font-semibold flex-1">{label}</div>
      <ArrowRight size={14} className="text-white/30 group-hover:text-rose-400 group-hover:translate-x-0.5 transition" />
    </Link>
  );
}
